import type { MediaItem } from '../models/types';

export class AudioService {
  private audio: HTMLAudioElement;
  private nextAudio: HTMLAudioElement | null = null;
  private queue: MediaItem[] = [];
  private currentIndex = -1;
  private listeners: Map<string, Function[]> = new Map();

  constructor() {
    this.audio = new Audio();
    this.setupEventListeners();
  }

  private setupEventListeners() {
    this.setupAudioEventListeners(this.audio);
  }

  private setupAudioEventListeners(audio: HTMLAudioElement) {
    audio.addEventListener('playing', () => {
      if (this.audio === audio) {
        this.emit('play');
        if ('mediaSession' in navigator) {
          navigator.mediaSession.setActionHandler('play', () => this.play());
          navigator.mediaSession.setActionHandler('pause', () => this.pause());
          navigator.mediaSession.setActionHandler('previoustrack', () => this.skipToPrevious());
          navigator.mediaSession.setActionHandler('nexttrack', () => this.skipToNext());
          navigator.mediaSession.setActionHandler('seekto', (details) => {
            if (details.seekTime !== undefined) {
              this.seekTo(details.seekTime);
            }
          });
          navigator.mediaSession.playbackState = 'playing';
        }
      }
    });

    audio.addEventListener('pause', () => {
      if (this.audio === audio) {
        this.emit('pause');
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'paused';
        }
      }
    });

    audio.addEventListener('error', (error) => {
      if (this.audio === audio) {
        this.emit('error', error);
      }
    });

    audio.addEventListener('timeupdate', () => {
      if (this.audio === audio) {
        this.emit('timeupdate', audio.currentTime, audio.duration);
        
        // Preload next track when we're within 10 seconds of the end
        if (audio.duration > 0 && 
            audio.currentTime > audio.duration - 10 && 
            !this.nextAudio && 
            this.currentIndex < this.queue.length - 1) {
          this.preloadNextTrack();
        }
      }
    });
    
    audio.addEventListener('ended', () => {
      if (this.audio === audio) {
        // Use preloaded audio for gapless playback if available
        if (this.nextAudio) {
          const oldAudio = this.audio;
          this.audio = this.nextAudio;
          this.nextAudio = null;
          
          // Clean up old audio
          oldAudio.pause();
          oldAudio.src = '';
          
          // Set up listeners on new audio
          this.setupAudioEventListeners(this.audio);
          
          // Start playing and update index
          this.currentIndex++;
          if (this.currentIndex < this.queue.length) {
            this.updateMediaSession(this.queue[this.currentIndex]);
            // Emit trackchanged so UI updates
            this.emit('trackchanged', this.currentIndex);
            this.audio.play().catch(err => {
              console.error('Error playing preloaded audio:', err);
              this.emit('error', err);
            });
          }
        } else {
          this.emit('ended');
        }
      }
    });
  }

  private preloadNextTrack() {
    if (this.currentIndex < this.queue.length - 1) {
      const nextItem = this.queue[this.currentIndex + 1];
      if (nextItem && nextItem.id && 
          (nextItem.id.startsWith('http://') || nextItem.id.startsWith('https://'))) {
        this.nextAudio = new Audio();
        this.nextAudio.preload = 'auto';
        this.nextAudio.addEventListener('error', () => {
          // If preload fails, clear it so ended handler falls back to normal playback
          this.nextAudio = null;
        });
        this.nextAudio.src = nextItem.id;
      }
    }
  }

  updateMediaSession(item: MediaItem) {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: item.title,
        artist: item.artist || '',
        album: item.album || '',
        artwork: item.artUri ? [{ src: item.artUri }] : [],
      });
    }
  }

  private emit(event: string, ...args: any[]) {
    const listeners = this.listeners.get(event) || [];
    listeners.forEach(listener => listener(...args));
  }

  on(event: string, listener: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(listener);
  }

  off(event: string, listener: Function) {
    const listeners = this.listeners.get(event) || [];
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }

  async updateQueue(queue: MediaItem[]): Promise<void> {
    // Clear preloaded audio if queue changes
    if (this.nextAudio) {
      this.nextAudio.pause();
      this.nextAudio.src = '';
      this.nextAudio = null;
    }
    this.queue = queue;
  }

  async addQueueItems(items: MediaItem[]): Promise<void> {
    this.queue.push(...items);
  }

  async insertQueueItem(index: number, item: MediaItem): Promise<void> {
    this.queue.splice(index, 0, item);
  }

  async removeQueueItemAt(index: number): Promise<void> {
    if (index === this.currentIndex) {
      await this.skipToNext();
    } else {
      // If removing the next track, clear preloaded audio
      if (index === this.currentIndex + 1 && this.nextAudio) {
        this.nextAudio.pause();
        this.nextAudio.src = '';
        this.nextAudio = null;
      }
    }
    this.queue.splice(index, 1);
    if (this.currentIndex > index) {
      this.currentIndex--;
    }
  }

  async removeRange(start: number, end: number): Promise<void> {
    // Check if we're removing the next track
    const nextTrackIndex = this.currentIndex + 1;
    if (nextTrackIndex >= start && nextTrackIndex < end && this.nextAudio) {
      this.nextAudio.pause();
      this.nextAudio.src = '';
      this.nextAudio = null;
    }
    this.queue.splice(start, end - start);
    if (this.currentIndex >= start && this.currentIndex < end) {
      this.currentIndex = -1;
    } else if (this.currentIndex >= end) {
      this.currentIndex -= (end - start);
    }
  }

  async play(): Promise<void> {
    if (this.queue.length > 0 && this.currentIndex >= 0) {
      const currentItem = this.queue[this.currentIndex];
      if (currentItem && currentItem.id) {
        // Validate that the URL looks like a valid audio URL
        if (!currentItem.id.startsWith('http://') && !currentItem.id.startsWith('https://')) {
          console.error('Invalid audio URL (must start with http:// or https://):', currentItem.id);
          this.emit('error', new Error(`Invalid audio URL: ${currentItem.id}`));
          return;
        }

        // Only set src if the track actually changes (fixes restart bug)
        if (this.audio.src !== currentItem.id) {
          this.audio.src = currentItem.id;
        }
        try {
          await this.audio.play();
          // MediaSession playbackState is set in the 'playing' event listener
        } catch (error) {
          console.error('Error playing audio:', error);
          this.emit('error', error);
          throw error;
        }
      } else {
        console.error('No valid audio item found at index:', this.currentIndex);
        this.emit('error', new Error('No valid audio item'));
      }
    }
  }

  async pause(): Promise<void> {
    this.audio.pause();
  }

  async stop(): Promise<void> {
    this.audio.pause();
    this.audio.currentTime = 0;
  }

  async skipToQueueItem(index: number): Promise<void> {
    if (index >= 0 && index < this.queue.length) {
      // Clear preloaded audio when jumping to a different position
      if (this.nextAudio) {
        this.nextAudio.pause();
        this.nextAudio.src = '';
        this.nextAudio = null;
      }
      this.currentIndex = index;
      this.updateMediaSession(this.queue[index]);
      await this.play();
    }
  }

  async skipToNext(): Promise<void> {
    if (this.currentIndex < this.queue.length - 1) {
      // Clear preloaded audio since user is manually skipping
      if (this.nextAudio) {
        this.nextAudio.pause();
        this.nextAudio.src = '';
        this.nextAudio = null;
      }
      this.currentIndex++;
      this.updateMediaSession(this.queue[this.currentIndex]);
      this.emit('trackchanged', this.currentIndex);
      await this.play();
    }
  }

  async skipToPrevious(): Promise<void> {
    if (this.currentIndex > 0) {
      // Clear preloaded audio since user is manually going back
      if (this.nextAudio) {
        this.nextAudio.pause();
        this.nextAudio.src = '';
        this.nextAudio = null;
      }
      this.currentIndex--;
      this.updateMediaSession(this.queue[this.currentIndex]);
      this.emit('trackchanged', this.currentIndex);
      await this.play();
    }
  }

  async seekTo(position: number): Promise<void> {
    this.audio.currentTime = position;
  }

  getQueue(): MediaItem[] {
    return [...this.queue];
  }

  getCurrentIndex(): number {
    return this.currentIndex;
  }

  getCurrentItem(): MediaItem | null {
    return this.currentIndex >= 0 ? this.queue[this.currentIndex] : null;
  }

  getDuration(): number {
    return this.audio.duration || 0;
  }

  getCurrentPosition(): number {
    return this.audio.currentTime || 0;
  }

  isPlaying(): boolean {
    return !this.audio.paused && !this.audio.ended;
  }

  dispose() {
    this.audio.pause();
    this.listeners.clear();
  }
}